import './index.scss';

import { useThemeInitialization } from 'hooks/useThemeInitialization';
import { CarouselController } from 'Modulor/Carousel/CarouselController';
import { CurrencySelectContainer } from 'Modulor/CurrencySelectContainer';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { store } from 'store';
import { useAppSelector } from 'store/hooks';
import { isInPopUpSelector, navigationKeySelector } from 'store/slices/global';

import { useBackNavigationHandler } from '../hooks/historyBack/useBackNavigationHandler';
import { useRedirectBack } from '../hooks/historyBack/useRedirectBack';
import { useFinalPerformanceReport } from '../hooks/useFinalPerformanceReport';
import { useFrameChangeHeight } from '../hooks/useFrameChangeHeight';
import { useResetUIOnRouteChange } from '../hooks/useResetUIOnRouteChange';
import { AppCoreProvider } from '../providers/AppCoreProvider/AppCoreProvider';
import { flowAdapter } from '../providers/AppCoreProvider/flowAdapter';
import { HeaderProvider } from '../providers/HeaderProvider';
import { MethodGroupingProvider } from '../providers/MethodGroupingProvider';
import { FlowTypes } from '../store/constants';
import { getLangBrand } from '../store/formatters';
import { getBasename, getWidgetLocation } from '../utils';
import { normalizePathname } from '../utils/normalizeUrl';
import ErrorBoundary from './components/ErrorPage/ErrorBoundary';
import Layout from './components/Layout';
import { NotificationsController as Notifications } from './components/Notifications/NotificationsController';
import SkeletonDefaultInit from './components/Skeleton/init/SkeletonDefaultInit';
import SkeletonInitPage from './components/Skeleton/init/SkeletonInitPage';
import MethodGroupingController from './MethodGrouping/MethodGroupingController';
import { RouterShell } from './routerShell';

const { pathname, search: initialSearch } = window.location;
const initialParams = new URLSearchParams(initialSearch);
const paramLang = initialParams.get('lang') || '';
const paramMode = initialParams.get('mode') || 'light';
const [lang, brand] = getLangBrand(paramLang);

const is3dsResult = pathname === '/3ds_result';
const isResultPage =
  is3dsResult ||
  [
    '/deposit/deposit-error',
    '/deposit/deposit-success',
    '/deposit/deposit-processing',
    '/deposit/deposit-await',
    '/withdrawal/withdrawal-success',
    '/withdrawal/withdrawal-error',
  ].includes(pathname);

const AppRoutes = () => {
  useResetUIOnRouteChange();
  useFrameChangeHeight();
  useFinalPerformanceReport();
  const navigationKey = useAppSelector(navigationKeySelector);

  const { handleBack, handleRestore } = useBackNavigationHandler();
  useRedirectBack({
    onBack: handleBack,
    onRestore: handleRestore,
  });
  return (
    <AppCoreProvider key={navigationKey}>
      <HeaderProvider>
        <MethodGroupingProvider>
          <Layout />
          <div className="screen-layer">
            <Notifications />
            <CurrencySelectContainer />
            <Suspense fallback={null}>
              <MethodGroupingController />
              <CarouselController />
            </Suspense>
            <RouterShell />
          </div>
        </MethodGroupingProvider>
      </HeaderProvider>
    </AppCoreProvider>
  );
};

const ModulorApp = () => {
  const [isAppReady, setIsAppReady] = useState(false);
  const isOpenedInPopup = useAppSelector(isInPopUpSelector);

  useEffect(() => {
    try {
      const basename = getBasename();
      const cleanPathname = basename !== '/' ? pathname.replace(basename, '') : pathname;
      const { isMainPage } = getWidgetLocation(normalizePathname(cleanPathname));
      if (!isMainPage) return;
      const flow =
        cleanPathname.includes('withdrawal') && !cleanPathname.includes('reject-withdrawal')
          ? FlowTypes.withdrawal
          : FlowTypes.deposit;

      const activeAdapter = flowAdapter[flow];

      if (activeAdapter && activeAdapter.initiate) {
        store.dispatch(activeAdapter.initiate(undefined));
      }
    } catch (e) {
      console.error('Failed to pre-fetch flow init data', e);
    }
  }, []);

  const suspenseFallback = useMemo(
    () => (isResultPage ? <SkeletonInitPage /> : <SkeletonDefaultInit showHeader />),
    [],
  );

  useThemeInitialization({
    brand,
    lang,
    setIsAppReady,
    mode: paramMode,
  });

  useEffect(() => {
    if (isOpenedInPopup) {
      document.body.classList.add('app-no-scroll');
    }
  }, [isOpenedInPopup]);

  if (!isAppReady) {
    return (
      <section className="app-modulor">
        <div className="app-modulor__inner">
          <section className="body-container">
            <SkeletonDefaultInit showHeader />
          </section>
        </div>
      </section>
    );
  }

  // fallback={suspenseFallback} - for loading locales
  return (
    <Suspense fallback={suspenseFallback}>
      <section className="app-modulor">
        <div className="app-modulor__inner">
          <section className="body-container">
            <ErrorBoundary>
              <BrowserRouter basename={getBasename()}>
                <AppRoutes />
              </BrowserRouter>
            </ErrorBoundary>
          </section>
        </div>
      </section>
    </Suspense>
  );
};

export default ModulorApp;
